import { createElement, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react'
import { glassMapUrl, supportsGlassLens } from '@/lib/glass-map'
import { cn } from '@/lib/utils'

type GlassTag = 'div' | 'header' | 'nav' | 'section' | 'form'

interface GlassProps extends HTMLAttributes<HTMLElement> {
  as?: GlassTag
  /** Corner radius in px; must match the element's rounded-* class. Capped at half the height. */
  radius: number
  children: ReactNode
}

const LENS = supportsGlassLens()

function useReducedTransparency() {
  const query = '(prefers-reduced-transparency: reduce)'
  const [reduced, setReduced] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

/**
 * Liquid Glass surface (iOS 27 default look). Chromium bends the backdrop at the rim with an SVG
 * displacement map sized to this element; other browsers get the frosted blur from `.glass`.
 * Only for floating controls over scrolling content, never for content itself.
 */
export function Glass({ as = 'div', radius, className, style, children, ...rest }: GlassProps) {
  const ref = useRef<HTMLElement>(null)
  const filterId = `glass-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`
  const [size, setSize] = useState<{ w: number; h: number } | null>(null)
  const reduced = useReducedTransparency()
  const lens = LENS && !reduced

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !lens) return
    const read = () => {
      const w = Math.round(el.offsetWidth)
      const h = Math.round(el.offsetHeight)
      setSize((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }))
    }
    read()
    const observer = new ResizeObserver(read)
    observer.observe(el)
    return () => observer.disconnect()
  }, [lens])

  const ready = lens && size && size.w > 0 && size.h > 0
  const r = ready ? Math.min(radius, size.h / 2, size.w / 2) : radius
  const large = ready && size.w * size.h > 60000
  const bezel = large ? 22 : Math.max(10, Math.min(16, Math.round(r * 0.6)))
  const blur = large ? 9 : 4.5
  const scale = Math.round(bezel * 1.9)
  const map = ready ? glassMapUrl(size.w, size.h, Math.round(r), bezel) : ''

  return createElement(
    as,
    {
      ...rest,
      ref,
      className: cn('glass', className),
      'data-lens': map ? '' : undefined,
      style: {
        ...style,
        borderRadius: radius,
        '--glass-lens': map ? `url(#${filterId})` : undefined,
        '--glass-blur': `${blur * 3}px`,
      } as CSSProperties,
    },
    map && size ? (
      <svg aria-hidden="true" width="0" height="0" style={{ position: 'absolute', width: 0, height: 0 }}>
        <filter
          id={filterId}
          x="0"
          y="0"
          width={size.w}
          height={size.h}
          filterUnits="userSpaceOnUse"
          primitiveUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feImage href={map} x="0" y="0" width={size.w} height={size.h} preserveAspectRatio="none" result="map" />
          <feGaussianBlur in="SourceGraphic" stdDeviation={blur} result="soft" />
          <feDisplacementMap in="soft" in2="map" scale={scale} xChannelSelector="R" yChannelSelector="G" result="dr" />
          <feColorMatrix in="dr" type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="r" />
          <feDisplacementMap in="soft" in2="map" scale={scale * 0.94} xChannelSelector="R" yChannelSelector="G" result="dg" />
          <feColorMatrix in="dg" type="matrix" values="0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0" result="g" />
          <feDisplacementMap in="soft" in2="map" scale={scale * 0.88} xChannelSelector="R" yChannelSelector="G" result="db" />
          <feColorMatrix in="db" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0" result="b" />
          <feBlend in="r" in2="g" mode="screen" result="rg" />
          <feBlend in="rg" in2="b" mode="screen" result="rgb" />
          <feColorMatrix in="rgb" type="saturate" values="1.6" />
        </filter>
      </svg>
    ) : null,
    children,
  )
}
